import { useEffect, useRef, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Mail, MailX, Loader2 } from "lucide-react";

/**
 * Reusable thread/messages panel.
 *
 * Self-contained: hits /messages/threads/{id}/messages on mount, posts
 * /messages/threads/{id}/send on submit, marks /read after fetch. Polls
 * every 15s while open. Used both inside the admin inbox and the parent
 * floating chat bubble.
 */
export default function MessageThread({ threadId, otherName, emailOptIn, onEmailOptInChange, height = "60vh" }) {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    if (!threadId) return;
    try {
      const { data } = await http.get(`/messages/threads/${threadId}/messages`);
      setMsgs(data.messages || []);
      await http.post(`/messages/threads/${threadId}/read`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await http.post(`/messages/threads/${threadId}/send`, { body: body.trim() });
      setMsgs((prev) => [...prev, data]);
      setBody("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const toggleEmail = async () => {
    const next = !emailOptIn;
    try {
      await http.post("/messages/email-opt-in", { enabled: next });
      onEmailOptInChange?.(next);
      toast.success(next ? "Email notifications on" : "Email notifications muted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="flex flex-col" style={{ height }} data-testid={`message-thread-${threadId}`}>
      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/8">
        <div>
          <div className="font-display font-bold uppercase text-sm leading-tight">{otherName || "Conversation"}</div>
          <div className="text-[10px] text-white/40 font-mono">
            Polled every 15s · Replies notify both sides via email
          </div>
        </div>
        <button
          onClick={toggleEmail}
          className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1"
          title={emailOptIn ? "Mute email notifications" : "Re-enable email notifications"}
          data-testid="email-opt-toggle"
        >
          {emailOptIn ? <Mail size={12} /> : <MailX size={12} />}
          <span className="text-[10px] uppercase tracking-wider font-mono">{emailOptIn ? "Email on" : "Muted"}</span>
        </button>
      </div>

      {/* messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#050505]" data-testid="message-list">
        {loading ? (
          <div className="text-white/30 text-center text-sm py-10">Loading…</div>
        ) : msgs.length === 0 ? (
          <div className="text-white/35 text-center text-sm py-10">
            No messages yet. Say hi 👋
          </div>
        ) : (
          msgs.map((m) => {
            const mine = ["super_admin", "tester_admin"].includes(m.sender_role)
              ? localStorage.getItem("role") === "super_admin" || localStorage.getItem("role") === "tester_admin"
              : localStorage.getItem("role") === m.sender_role;
            const isAdmin = ["super_admin", "tester_admin"].includes(m.sender_role);
            return (
              <div
                key={m.id}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[78%] rounded-lg px-3 py-2 ${
                    mine
                      ? "bg-electric text-black"
                      : "bg-[#0e0e0e] border border-white/10 text-white"
                  }`}
                  data-testid={`msg-${m.id}`}
                >
                  <div className="text-[10px] uppercase tracking-wider font-mono opacity-70 mb-0.5">
                    {isAdmin ? "VERIFIED Team" : (m.sender_name || "Parent")} ·{" "}
                    {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </div>
                  <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* composer */}
      <form onSubmit={send} className="flex gap-2 p-2 border-t border-white/8 bg-[#0a0a0a]" data-testid="message-composer">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(e);
            }
          }}
          rows={2}
          placeholder="Type a message — Enter to send, Shift+Enter for newline"
          className="input-dark flex-1 rounded-md px-3 py-2 text-sm resize-none"
          data-testid="message-input"
          maxLength={4000}
        />
        <button
          type="submit"
          disabled={sending || !body.trim()}
          className="btn-primary px-3 self-stretch disabled:opacity-50"
          data-testid="message-send"
        >
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>
    </div>
  );
}
